// Parameters shared by every event inside a part of the page — the thread_id of a learn session, the video_id
// of one of its blocks — declared as a `data-tracking-context` attribute on the enclosing element and merged
// into every push that names an element inside it, by the sink (util/analytics.ts). Nearer ancestors win.
//
// Declared in markup on purpose: SSR renders it, the client updates it, and no state lives in a module.

export type TrackingContext = Record<string, unknown>

export const TRACKING_CONTEXT_ATTRIBUTE = 'data-tracking-context'

/**
 * The attribute value for a context, for `data-tracking-context={trackingContextAttribute({ video_id })}`.
 * `undefined` values are dropped; with nothing left the result is `undefined`, so no attribute renders.
 */
export function trackingContextAttribute(context: TrackingContext): string | undefined {
  const entries = Object.entries(context).filter(([, value]) => value !== undefined)
  return entries.length > 0 ? JSON.stringify(Object.fromEntries(entries)) : undefined
}

export function domTrackingContext(element: Element): TrackingContext {
  const layers: TrackingContext[] = []
  let carrier = element.closest(`[${TRACKING_CONTEXT_ATTRIBUTE}]`)

  while (carrier) {
    layers.unshift(parse(carrier.getAttribute(TRACKING_CONTEXT_ATTRIBUTE)))
    carrier = carrier.parentElement?.closest(`[${TRACKING_CONTEXT_ATTRIBUTE}]`) ?? null
  }

  return Object.assign({}, ...layers)
}

function parse(json: string | null): TrackingContext {
  if (!json) {
    return {}
  }
  try {
    const value: unknown = JSON.parse(json)
    return value !== null && typeof value === 'object' ? (value as TrackingContext) : {}
  } catch {
    return {}
  }
}
